import { Box, Typography, useTheme } from "@mui/material";
import { keyframes } from "@emotion/react";
import React, { useEffect, useState } from "react";
import styles from "./loader.module.scss";
import ParticleBackground from "./ParticleBackground";

// Global style to hide cursor during loading - scoped to loader container
const noCursorStyle = `
  .full-page-loader-container * {
    caret-color: transparent !important;
    cursor: default !important;
  }
`;

interface FullPageLoaderProps {
  message?: string;
  loadingMessages?: string[];
}

const pulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
`;

const defaultLoadingMessages = [
  "Loading your personalized dashboard...",
  "Preparing your account information...",
  "Checking your permissions...",
  "Setting up your workspace...",
  "Almost there..."
];

const FullPageLoader: React.FC<FullPageLoaderProps> = ({ 
  message,
  loadingMessages = defaultLoadingMessages
}) => {
  const theme = useTheme();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [activeDot, setActiveDot] = useState(0);
  const displayMessage = message || loadingMessages[currentMessageIndex];

  useEffect(() => {
    // Cycle through loading messages
    const messageTimer = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => 
        (prevIndex + 1) % loadingMessages.length
      );
      setActiveDot((prevDot) => (prevDot + 1) % 3);
    }, 3000);

    // Progress bar animation
    const progressTimer = setInterval(() => {
      setProgress((prevProgress) => {
        // Slow down progress as it gets closer to 100%
        const increment = (100 - prevProgress) / 20;
        return Math.min(prevProgress + (increment || 1), 95);
      });
    }, 800);

    // Inject style to prevent cursor - scoped to loader
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-component', 'full-page-loader');
    styleElement.innerHTML = noCursorStyle;
    document.head.appendChild(styleElement);

    return () => {
      clearInterval(messageTimer);
      clearInterval(progressTimer);
      // Ensure cleanup happens even if component unmounts unexpectedly
      try {
        if (styleElement && document.head.contains(styleElement)) {
          document.head.removeChild(styleElement);
        }
      } catch (error) {
        console.warn('FullPageLoader: Could not remove style element:', error);
      }
    };
  }, [loadingMessages.length]);

  return (
    <Box
      className={`${styles.loaderContainer} full-page-loader-container`}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(18, 18, 18, 0.95)'
          : 'rgba(250, 250, 250, 0.95)',
        zIndex: 9999,
        cursor: "default",
        caretColor: "transparent",
      }}
      tabIndex={-1}
      aria-hidden="true"
    >
      {/* Particle Background */}
      <ParticleBackground />
      
      <Box
        className={styles.loaderBox}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          padding: 4,
          borderRadius: 4,
          maxWidth: "90%",
          width: "450px",
          textAlign: "center",
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(30, 30, 30, 0.8)'
            : 'rgba(255, 255, 255, 0.8)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 5px rgba(255, 255, 255, 0.05)'
            : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 5px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${theme.palette.divider}`,
          cursor: "default",
          caretColor: "transparent",
        }}
        tabIndex={-1}
        aria-hidden="true"
      >
        {/* Logo container with animation */}
        <Box 
          className={styles.logoContainer}
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            mb: 2,
            userSelect: "none",
            cursor: "default",
            caretColor: "transparent",
          }}
          tabIndex={-1}
          aria-hidden="true"
        >
          {/* Logo - replace with your actual logo import */}
          <Box
            component="img"
            src="/logo.png"
            alt="Incomlend"
            draggable="false"
            sx={{
              width: "200px",
              height: "auto",
              mb: 3,
              animation: `${pulse} 2s infinite ease-in-out`,
              filter: theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'none',
              pointerEvents: "none",
              userSelect: "none",
              cursor: "default",
            }}
            tabIndex={-1}
            aria-hidden="true"
          />
        </Box>
        
        {/* Custom SVG Loader */}
        <svg 
          className={styles.svgLoader} 
          viewBox="0 0 50 50"
          aria-hidden="true"
          tabIndex={-1}
        >
          <circle
            className={styles.svgLoaderCircle}
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth="4"
            stroke={theme.palette.primary.main}
          />
        </svg>
        
        {/* Progress bar */}
        <Box 
          className={styles.progressBarContainer}
          tabIndex={-1}
          aria-hidden="true"
        >
          <Box 
            className={styles.progressBarFill} 
            sx={{ width: `${progress}%` }} 
          />
        </Box>
        
        <Typography 
          variant="h6" 
          sx={{ 
            mt: 1,
            fontWeight: 500,
            color: theme.palette.text.primary,
            userSelect: "none",
            cursor: "default",
            caretColor: "transparent",
          }}
          tabIndex={-1}
          aria-hidden="true"
        >
          {displayMessage}
        </Typography>
        
        {/* Loading dots */}
        <Box 
          className={styles.dots}
          tabIndex={-1}
          aria-hidden="true"
        >
          <Box 
            className={`${styles.dot} ${activeDot === 0 ? styles.active : ''}`}
            sx={{ color: theme.palette.primary.main }}
          />
          <Box 
            className={`${styles.dot} ${activeDot === 1 ? styles.active : ''}`}
            sx={{ color: theme.palette.primary.main }}
          />
          <Box 
            className={`${styles.dot} ${activeDot === 2 ? styles.active : ''}`}
            sx={{ color: theme.palette.primary.main }}
          />
        </Box>
        
        <Typography 
          variant="body2"
          sx={{ 
            mt: 2,
            opacity: 0.8,
            maxWidth: "85%",
            color: theme.palette.text.secondary,
            userSelect: "none",
            cursor: "default",
            caretColor: "transparent",
          }}
          tabIndex={-1}
          aria-hidden="true"
        >
          We're preparing your personalized experience with the most up-to-date information.
        </Typography>
      </Box>
    </Box>
  );
};

export default FullPageLoader;
